// Zebra Expr — panel-markup → styled-cell conversion.
//
// Formulas that build the string `:panel[TEXT]{backgroundColor=…, color=…}`
// (a `format(text,color)` helper is the common idiom) should render as a
// coloured cell, not raw text. This module converts that markup to the
// styled-cell shape the grid/totals render.
//
// Pure — no engine or frontend dependencies — so it is the SINGLE source of
// truth shared by the client (expr/index.js) and the server-side DSL evaluator
// (modules/folders/server/stride/dsl-eval.js dynamic-imports this file). Keep
// it dependency-free so the server import stays cheap and CJS-safe.

const NAMED_COLORS = {
  white: [255, 255, 255], black: [0, 0, 0], red: [255, 0, 0], green: [0, 128, 0],
  blue: [0, 0, 255], orange: [255, 165, 0], yellow: [255, 255, 0], lime: [0, 255, 0],
  lightgrey: [211, 211, 211], lightgray: [211, 211, 211], grey: [128, 128, 128], gray: [128, 128, 128],
  darkgrey: [64, 64, 64], darkgray: [64, 64, 64], silver: [192, 192, 192],
  lightgreen: [144, 238, 144], lightblue: [173, 216, 230], lightyellow: [255, 255, 224],
  pink: [255, 192, 203], purple: [128, 0, 128], teal: [0, 128, 128], cyan: [0, 255, 255],
  navy: [0, 0, 128], maroon: [128, 0, 0], olive: [128, 128, 0], gold: [255, 215, 0],
  salmon: [250, 128, 114], coral: [255, 127, 80], khaki: [240, 230, 140], brown: [165, 42, 42],
};
function parseColor(c) {
  const s = String(c).trim().toLowerCase();
  if (s.startsWith('#')) {
    let h = s.slice(1);
    if (h.length === 3) h = h.split('').map((x) => x + x).join('');
    if (h.length >= 6) {
      const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
      if (![r, g, b].some(Number.isNaN)) return [r, g, b];
    }
    return null;
  }
  const rgb = s.match(/rgba?\(([^)]+)\)/);
  if (rgb) {
    const p = rgb[1].split(',').map((x) => parseInt(x, 10));
    if (p.length >= 3 && !p.slice(0, 3).some(Number.isNaN)) return p.slice(0, 3);
  }
  return NAMED_COLORS[s] || null;
}
// Readable text colour for a given background (YIQ perceived brightness): dark
// ink on light fills, white on dark ones. Returns null if the colour is unknown.
function contrastText(bg) {
  const rgb = parseColor(bg);
  if (!rgb) return null;
  const yiq = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  return yiq >= 140 ? '#1a2330' : '#ffffff';
}
// Ensure a coloured cell's number stays legible: if it has a background but no
// explicit text colour, pick a contrasting one and give it a touch of weight.
function ensureReadable(cell) {
  if (cell && typeof cell === 'object' && cell.backgroundColor && !cell.color) {
    const color = contrastText(cell.backgroundColor);
    if (color) return { ...cell, color, fontWeight: cell.fontWeight || '600' };
  }
  return cell;
}

const PANEL_RE = /^\s*:panel\[([\s\S]*?)\]\{([\s\S]*)\}\s*$/;
export function panelToStyled(value) {
  if (value && typeof value === 'object' && 'text' in value) return ensureReadable(value);
  if (typeof value !== 'string') return value;
  const m = value.match(PANEL_RE);
  if (!m) return value;
  const cell = { text: m[1] };
  for (const part of m[2].split(',')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    const key = part.slice(0, eq).trim().toLowerCase();
    const val = part.slice(eq + 1).trim();
    if (!val) continue;
    if (key === 'backgroundcolor' || key === 'background' || key === 'bg') cell.backgroundColor = val;
    else if (key === 'color' || key === 'textcolor' || key === 'fontcolor') cell.color = val;
    else if (key === 'fontweight' || key === 'weight') cell.fontWeight = val;
    else if (key === 'bold') cell.fontWeight = 'bold';
  }
  return ensureReadable(cell);
}
